import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError, ApiResponse } from '@matchroom/shared';
import { API_ROUTES } from '@matchroom/shared';
import { env } from './env';
import { getApiBaseUrl } from './dev-url';
import { resolveAppOrigin } from './runtime-url';

export class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

type TokenGetter = () => string | null;
type TokenSetter = (token: string) => void;
type AuthClearer = () => void;

let getAccessToken: TokenGetter = () => null;
let setAccessToken: TokenSetter = () => {};
let clearAuth: AuthClearer = () => {};

export function configureAuthHandlers(handlers: {
  getAccessToken: TokenGetter;
  setAccessToken: TokenSetter;
  clearAuth: AuthClearer;
}) {
  getAccessToken = handlers.getAccessToken;
  setAccessToken = handlers.setAccessToken;
  clearAuth = handlers.clearAuth;
}

const CSRF_COOKIE = 'matchroom_csrf';
const CSRF_HEADER = 'x-csrf-token';
const SAFE_METHODS = new Set(['get', 'head', 'options']);

function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function getApiBaseUrlResolved(): string {
  if (import.meta.env.DEV) {
    return getApiBaseUrl();
  }
  return resolveAppOrigin(env.VITE_API_URL);
}

export const api = axios.create({
  baseURL: getApiBaseUrlResolved(),
  withCredentials: true,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const method = config.method?.toLowerCase() ?? 'get';
  if (!SAFE_METHODS.has(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers[CSRF_HEADER] = csrfToken;
    }
  }

  return config;
});

let isRefreshing = false;
let refreshBlocked = false;
let refreshQueue: Array<(token: string | null) => void> = [];

function processQueue(token: string | null) {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    if (status === 429) {
      refreshBlocked = true;
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !refreshBlocked &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/register') &&
      !originalRequest.url?.includes('/auth/guest') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push((token) => {
            if (!token) {
              reject(error);
              return;
            }
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post<ApiResponse<{ tokens: { accessToken: string; expiresIn: number } }>>(
          API_ROUTES.AUTH.REFRESH,
        );
        if (data.success && data.data?.tokens.accessToken) {
          const newToken = data.data.tokens.accessToken;
          setAccessToken(newToken);
          processQueue(newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
        clearAuth();
        processQueue(null);
        return Promise.reject(error);
      } catch (refreshError) {
        if (axios.isAxiosError(refreshError) && refreshError.response?.status === 429) {
          refreshBlocked = true;
        }
        clearAuth();
        processQueue(null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

function handleResponse<T>(response: { data: ApiResponse<T> }): T {
  const { data } = response;
  if (!data.success || data.data === undefined) {
    const err = data.error as ApiError;
    throw new ApiClientError(
      err?.code ?? 'UNKNOWN_ERROR',
      err?.message ?? 'An unexpected error occurred',
      400,
      err?.details,
    );
  }
  return data.data;
}

function handleAxiosError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      const isTimeout = error.code === 'ECONNABORTED';
      throw new ApiClientError(
        'NETWORK_ERROR',
        isTimeout
          ? 'The server took too long to respond. Please try again.'
          : typeof window !== 'undefined' &&
              !window.location.hostname.includes('localhost') &&
              !window.location.hostname.startsWith('192.168.')
            ? "Can't reach the MatchRoom API. The site loaded but the server API is not responding."
            : "Can't reach the server right now. Check your connection and try again.",
        0,
      );
    }
    const apiError = error.response?.data?.error as ApiError | undefined;
    throw new ApiClientError(
      apiError?.code ?? 'UNKNOWN_ERROR',
      apiError?.message ?? error.message,
      error.response?.status ?? 500,
      apiError?.details,
    );
  }
  throw error;
}

export const apiClient = {
  get: async <T>(url: string) => {
    try {
      return handleResponse<T>(await api.get<ApiResponse<T>>(url));
    } catch (e) {
      handleAxiosError(e);
    }
  },
  post: async <T>(url: string, body?: unknown) => {
    try {
      return handleResponse<T>(await api.post<ApiResponse<T>>(url, body));
    } catch (e) {
      handleAxiosError(e);
    }
  },
  patch: async <T>(url: string, body?: unknown) => {
    try {
      return handleResponse<T>(await api.patch<ApiResponse<T>>(url, body));
    } catch (e) {
      handleAxiosError(e);
    }
  },
  delete: async <T>(url: string, body?: unknown) => {
    try {
      return handleResponse<T>(await api.delete<ApiResponse<T>>(url, body ? { data: body } : undefined));
    } catch (e) {
      handleAxiosError(e);
    }
  },
};
